import type { SelectOption } from "@circleco/compass/components/Select";

export interface AsyncSearchResponse {
  has_next_page: boolean;
  results: Array<{ text: string; value: string | number }>;
}

interface SearchFilterOptionsParams {
  cursor: number | null;
  fetchJson: (
    url: string,
    options: { signal?: AbortSignal },
  ) => Promise<AsyncSearchResponse>;
  query: string;
  signal?: AbortSignal;
  url: string;
  values?: string[];
}

/** Adds search and pagination parameters without changing the signed token. */
const buildAsyncSearchUrl = (
  url: string,
  query: string,
  page: number,
  values: string[] = [],
): string => {
  const searchUrl = new URL(url, window.location.origin);

  searchUrl.searchParams.set("q", query);
  searchUrl.searchParams.set("page", String(page));
  values.forEach(value => searchUrl.searchParams.append("values[]", value));

  return searchUrl.toString();
};

/** Converts one backend result page to the cursor contract used by Compass. */
export const searchFilterOptions = async ({
  cursor,
  fetchJson,
  query,
  signal,
  url,
  values,
}: SearchFilterOptionsParams): Promise<{
  items: SelectOption<string>[];
  hasMore: boolean;
  nextCursor: number | undefined;
}> => {
  const page = cursor ?? 1;
  const response = await fetchJson(
    buildAsyncSearchUrl(url, query, page, values),
    { signal },
  );

  return {
    // Values are opaque strings so options match values restored from the URL.
    items: response.results.map(({ text, value }) => ({
      label: text,
      value: String(value),
    })),
    hasMore: response.has_next_page,
    nextCursor: response.has_next_page ? page + 1 : undefined,
  };
};
